import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Text,
    Link,
    Image,
    Skeleton,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useIntl, FormattedNumber, FormattedMessage } from 'react-intl'

/* Hooks */
import { useCurrency } from '@salesforce/retail-react-app/app/hooks'

/* Icons */
import { StandardShipping, ExpressShipping, UberShipping, InStoreShipping } from '../../../components/icons'

/* Utils */
import { generateStyles } from '../../../components/optimizely/utils/utils'

const DeliveryOptions = ({ shippingMethodsData = {}, cmsDeliverySectionBlock, isLoading = false }) => {
    const styles = useMultiStyleConfig('DeliveryOptions')
    const { formatMessage } = useIntl()
    const { currency } = useCurrency()
    const [shippingMethods, setShippingMethods] = useState({})
    const [smLimit, setSmLimit] = useState(null)
    const [initialSmLimit, setInitialSmLimit] = useState(null)
    const [showMore, setShowMore] = useState(true)
    const [smCMSResourceMapping, setSmCMSResourceMapping] = useState(null)
    const [cmsNotes, setCmsNotes] = useState([])

    const CMSDeliveryKeyMapping = {
        "standard": "Standard_Ground",
        "express": "Express_Shipping",
        "uber": "SameDay_Shipping",
        "same_day_pickup": "SameDay_Pickup"
    }
    /* In case new shipping methods will be returned from the API this object must be updated accordingly */
    const smResourceMapping = {
        "standard": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.standardMethod',
                defaultMessage: 'Standard Shipping'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => formatMessage({
                id: 'pdp_product_detail.sm.description.standardMethod',
                defaultMessage: 'FREE for orders $60 & up'
            }),
            cost: 0,
            icon: () => (<StandardShipping />)
        },
        "express": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.expressMethod',
                defaultMessage: 'Express Shipping'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => '',
            cost: 10,
            icon: () => (<ExpressShipping width={'36px'} />)
        },
        "priority": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.priorityMethod',
                defaultMessage: 'Priority Shipping'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => '',
            cost: 20,
            icon: () => (<ExpressShipping width={'36px'} />)
        },
        "accelerated": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.acceleratedGround',
                defaultMessage: 'Accelerated Ground'
            }),
            arrival: (low = '', high = '') => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival"
                    defaultMessage="Arrives in <span>{low}-{high} business days</span>"
                    values={{
                        low,
                        high,
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => '',
            cost: 7,
            icon: () => (<ExpressShipping width={'36px'} />)
        },
        "uber": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.uber',
                defaultMessage: 'Same-Day Shipping*'
            }),
            arrival: () => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival.uber"
                    defaultMessage="<span>Get it today!</span>"
                    values={{
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => formatMessage({
                id: 'pdp_product_detail.sm.description.uber',
                defaultMessage: 'For orders placed by 3:00 p.m.'
            }),
            cost: 10,
            icon: () => (<UberShipping width={'39px'} />)
        },
        "same_day_pickup": {
            name: formatMessage({
                id: 'pdp_product_detail.sm.name.pick_up_in_store',
                defaultMessage: 'Same-Day Pickup in Store**'
            }),
            arrival: () => {
                return <FormattedMessage
                    id="pdp_product_detail.sm.arrival.pick_up_in_store"
                    defaultMessage="<span>Available within 2 hours</span>"
                    values={{
                        span: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                    }}
                />
            },
            description: () => formatMessage({
                id: 'pdp_product_detail.sm.description.pick_up_in_store',
                defaultMessage: 'For orders placed by 3:00 p.m.'
            }),
            cost: 0,
            icon: () => (<InStoreShipping />)
        }
    }

    const freeLabel = formatMessage({
        defaultMessage: 'Free',
        id: 'checkout_confirmation.label.free'
    })

    const generateDeliveryOptionsFromCMS = (filteredShippingMethods, deliveryOptionList, CMSDeliveryKeyMapping) => {
        // Get keys from filteredShippingMethods
        const keys = Object.keys(filteredShippingMethods);

        // Filter DeliveryOptionList by matching DeliveryKey
        const filteredList = deliveryOptionList.filter(option => {
            const block = option.ContentLink?.Expanded;
            if (!block) return false;
            // Find if any key maps to this DeliveryKey
            return keys.some(key => CMSDeliveryKeyMapping[key] === block.DeliveryKey);
        });

        // Map to new object
        const result = {};
        filteredList.forEach(option => {
            const block = option.ContentLink?.Expanded;
            if (!block) return;
            // Find the key in filteredShippingMethods that matches this DeliveryKey
            const key = keys.find(k => CMSDeliveryKeyMapping[k] === block.DeliveryKey);
            if (!key) return;

            result[key] = {
                name: block.Title,
                arrival: (low, high) => block.DescriptionFirstLine
                    ?.replace('{low}', low)
                    ?.replace('{high}', high),
                description: () => block.DescriptionSecondLine,
                cost: block.Price,
                icon: () => (<Image margin={'0 auto'} src={block.Icon?.Expanded?.Url} alt={block?.IconMediaAltText} />),
                notes: block.Notes,
                IsCollapsible: block.IsCollapsible,
                DeliveryOptionBlockTitleStyle: block.DeliveryOptionBlockTitleStyle,
                DescriptionFirstLineStyle: block.DescriptionFirstLineStyle,
                DescriptionSecondLineStyle: block.DescriptionSecondLineStyle,
                DeliveryOptionBlockPriceStyle: block.DeliveryOptionBlockPriceStyle,
                NotesStyle: block.NotesStyle
            };
        });

        return result;
    }

    useEffect(() => {
        // Filter shippingMethods to include only those present in smResourceMapping
        // If new delivery option will be returned from BD the smResourceMapping must be updated accordingly
        if (shippingMethodsData) {
            const filteredShippingMethods = Object.fromEntries(
                Object.entries(shippingMethodsData).filter(([key]) => key in smResourceMapping)
            );

            setShippingMethods(filteredShippingMethods)
        }
    }, [shippingMethodsData])

    useEffect(() => {
        if (shippingMethods && cmsDeliverySectionBlock) {
            const cmsDeliveryOptions = generateDeliveryOptionsFromCMS(shippingMethods, cmsDeliverySectionBlock?.DeliveryOptionList, CMSDeliveryKeyMapping)
            const cmsDeliveryOptionsValues = Object.values(cmsDeliveryOptions)
            const limit = cmsDeliveryOptionsValues.filter(opt => opt.IsCollapsible === true).length
            setSmLimit(limit)
            setInitialSmLimit(limit)
            setShowMore(limit > 0)
            setCmsNotes(
                cmsDeliveryOptionsValues
                    .map(opt =>
                        Array.isArray(opt.notes)
                            ? opt.notes.map(note => ({ note, NotesStyle: opt.NotesStyle }))
                            : []
                    )
                    .flat()
                    .filter(obj => obj.note && obj.note.length)
            )
            setSmCMSResourceMapping(cmsDeliveryOptions)
        } else {
            setSmLimit(2)
            setInitialSmLimit(2)
        }
    }, [cmsDeliverySectionBlock, shippingMethods])

    if (isLoading) {
        const cmsOptions = cmsDeliverySectionBlock?.DeliveryOptionList
        if (cmsOptions) {
            return (
                <Box>
                    {cmsOptions.map((option) => {
                        const block = option.ContentLink?.Expanded
                        if (!block) return null
                        return (
                            <Box key={block.DeliveryKey} {...styles.container}>
                                <Box {...styles.icon}>
                                    {block.Icon?.Expanded?.Url ? (
                                        <Image margin={'0 auto'} src={block.Icon.Expanded.Url} alt={block.IconMediaAltText} />
                                    ) : (
                                        <Skeleton width="36px" height="36px" borderRadius="md" />
                                    )}
                                </Box>
                                <Box>
                                    <Text {...generateStyles(block.DeliveryOptionBlockTitleStyle, { ...styles.name })}>
                                        {block.Title}
                                    </Text>
                                    <Text {...generateStyles(block.DescriptionFirstLineStyle, { ...styles.typo })}
                                        dangerouslySetInnerHTML={{
                                            __html: block.DescriptionFirstLine
                                                ?.replace('{low}', '<span style="display:inline-block;width:12px;height:12px;border-radius:4px;background:#e2e2e2;vertical-align:middle"></span>')
                                                ?.replace('{high}', '<span style="display:inline-block;width:12px;height:12px;border-radius:4px;background:#e2e2e2;vertical-align:middle"></span>')
                                        }}
                                    />
                                    {block.DescriptionSecondLine && (
                                        <Text {...generateStyles(block.DescriptionSecondLineStyle, { ...styles.typo })}
                                            dangerouslySetInnerHTML={{ __html: block.DescriptionSecondLine }}
                                        />
                                    )}
                                </Box>
                                <Box {...styles.costWrapper}>
                                    <Skeleton height="14px" width="40px" />
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            )
        }

        // Fallback when no CMS data available
        return (
            <Box>
                {Object.keys(smResourceMapping).map((key) => {
                    const sm = smResourceMapping[key]
                    return (
                        <Box key={key} {...styles.container}>
                            <Box {...styles.icon}>
                                {sm.icon()}
                            </Box>
                            <Box>
                                <Text {...styles.name}>
                                    {sm.name}
                                </Text>
                                <Text {...styles.typo}>
                                    <FormattedMessage
                                        id="pdp_product_detail.sm.arrival.skeleton"
                                        defaultMessage="Arrives in {skeleton} business days"
                                        values={{
                                            skeleton: <Skeleton as="span" display="inline-block" width="30px" height="12px" verticalAlign="middle" />
                                        }}
                                    />
                                </Text>
                                {sm.description() && (
                                    <Text {...styles.typo}>
                                        {sm.description()}
                                    </Text>
                                )}
                            </Box>
                            <Box {...styles.costWrapper}>
                                <Skeleton height="14px" width="40px" />
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        )
    }

    return (
        shippingMethodsData &&
        <Box>
            {Object.keys(shippingMethodsData)?.slice(0, smLimit > 0 ? smLimit : Object.keys(shippingMethodsData)?.length)?.map((key) => {
                const smData = shippingMethods[key]
                const sm = smCMSResourceMapping ? smCMSResourceMapping[key] : smResourceMapping[key]

                return (sm && smData &&
                    <Box key={key} {...styles.container}>
                        <Box {...styles.icon}>
                            {sm.icon()}
                        </Box>

                        <Box>
                            <Text {...generateStyles(sm?.DeliveryOptionBlockTitleStyle, { ...styles.name })} >
                                {sm.name}
                            </Text>
                            {smCMSResourceMapping ? (
                                <Text
                                    {...generateStyles(sm?.DescriptionFirstLineStyle, { ...styles.typo })}
                                    dangerouslySetInnerHTML={{ __html: sm.arrival(smData?.low || '', smData?.high || '') }}
                                />
                            ) : (
                                <Text {...generateStyles(sm?.DescriptionFirstLineStyle, { ...styles.typo })} >
                                    {sm.arrival(smData?.low || '', smData?.high || '')}
                                </Text>
                            )}
                            {smCMSResourceMapping ? (
                                <Text
                                    {...generateStyles(sm?.DescriptionSecondLineStyle, { ...styles.typo })}
                                    dangerouslySetInnerHTML={{ __html: sm.description() }}
                                />
                            ) : (
                                <Text {...generateStyles(sm?.DescriptionSecondLineStyle, { ...styles.typo })}>
                                    {sm.description()}
                                </Text>
                            )}
                        </Box>

                        <Box {...styles.costWrapper}>
                            <Text {...generateStyles(sm?.DeliveryOptionBlockPriceStyle, { ...styles.cost })}>
                                {smData.hasOwnProperty('cost') && smData.cost === 0 ||
                                    !smData.hasOwnProperty('cost') && sm.cost === 0 ? (
                                    freeLabel
                                ) : (
                                    typeof (smData.cost || sm.cost) === 'string' ? (
                                        smData.cost || sm.cost
                                    ) : (
                                        <FormattedNumber
                                            value={smData.cost || sm.cost}
                                            style="currency"
                                            currency={currency}
                                        />
                                    )
                                )}
                            </Text>
                        </Box>
                    </Box>
                )
            })}

            {!showMore &&
                (cmsNotes?.length > 0 ? (
                    <Box mb={{ base: 4 }}>
                        {cmsNotes?.map((note, index) => (
                            <Text
                                key={index}
                                {...generateStyles(note?.NotesStyle, { ...styles.typo })}
                                dangerouslySetInnerHTML={{ __html: note?.note }}
                            />
                        ))}
                    </Box>
                ) : null)
            }

            {showMore ? (
                <Button variant={'link'}
                    as={Link}
                    href='#'
                    aria-label='See more content from the Shipping & Pickup Options'
                    onClick={(e) => {
                        if (e) e.preventDefault()
                        setSmLimit(Object.keys(shippingMethods).length)
                        setShowMore(false)
                    }}
                    {...styles.typo}>
                    <FormattedMessage
                        defaultMessage="See More"
                        id="product_detail.shipping_methods.see_more"
                    />
                </Button>
            ) : (
                smLimit > 0 && (
                    <Button variant={'link'}
                        as={Link}
                        href='#'
                        aria-label='See less content from the Shipping & Pickup Options'
                        onClick={(e) => {
                            if (e) e.preventDefault()
                            setSmLimit(initialSmLimit)
                            setShowMore(true)
                        }}
                        {...styles.typo}>
                        <FormattedMessage
                            defaultMessage="See Less"
                            id="product_detail.shipping_methods.see_less"
                        />
                    </Button>
                )
            )}
        </Box>
    )
}

DeliveryOptions.propTypes = {
    shippingMethodsData: PropTypes.object.isRequired,
    cmsDeliverySectionBlock: PropTypes.object,
    isLoading: PropTypes.bool,
}

export default DeliveryOptions
